import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Table, Spinner } from 'react-bootstrap';
import { useFirestore } from '../contexts/FirestoreContext';
import { useAuth } from '../contexts/AuthContext';

// ===== helpers =====
const toDate = (ts) => (ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null));
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
const fmtMD = (d) => (d ? `${d.getMonth() + 1}/${d.getDate()}` : '');
const fmtRange = (a, b) => `${(a.getMonth() + 1)}/${a.getDate()}/${a.getFullYear()} Thru ${(b.getMonth() + 1)}/${b.getDate()}/${b.getFullYear()}`;
const formatYYYYMMDD = (d) => { if (!d) return ''; const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, '0'); const day = String(d.getDate()).padStart(2, '0'); return `${y}-${m}-${day}`; };
const parseDateInput = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

const stockDisplay = (v) =>
  v?.strStockNbr ? v.strStockNbr :
  (v?.strVin?.length >= 8 ? v.strVin.slice(-8) : (v?.strVin || ''));

const resolveModelName = (v, modelCodes = {}) => {
  const factory = (v?.strFactory ?? '').trim();
  const model = (v?.strModel ?? '').trim();
  const k1 = `${factory} - ${model}`;
  const k2 = `${factory} - ${model.substring(0, 3)}`;
  return modelCodes[k1] || modelCodes[k2] || model || '';
};

const GROUPS = [
  { value: 'date', label: 'By Date' },
  { value: 'customer', label: 'By Customer' },
  { value: 'model', label: 'By Model' },
  { value: 'salesOrder', label: 'By Sales Order' },
];

// Extract role from Firebase custom attributes on the client user object
const getUserRoleFromCurrentUser = (currentUser) => {
  try {
    const raw = currentUser?.reloadUserInfo?.customAttributes;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.role || null;
  } catch {
    return null;
  }
};

export default function ETAListByRange({ onlyActiveOrders = true }) {
  const { onSnapshotData, getData } = useFirestore();
  const { currentUser } = useAuth();

  const [vehicles, setVehicles] = useState([]);
  const [salesOrders, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [modelCodes, setModelCodes] = useState({});
  const [loading, setLoading] = useState(true);

  // role gates
  const [userRole, setUserRole] = useState(null);
  const [allowedCustNbrs, setAllowedCustNbrs] = useState(null);       // for Customer role
  const [allowedUpfitterNbr, setAllowedUpfitterNbr] = useState(null); // for Upfitter role

  const today = startOfDay(new Date());
  const defaultEnd = new Date(today); defaultEnd.setDate(defaultEnd.getDate() + 45);

  const [beginDate, setBeginDate] = useState(today);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [groupBy, setGroupBy] = useState('date');

  // live data
  useEffect(() => {
    const uV = onSnapshotData('vehicles', (v) => setVehicles(v || []));
    const uS = onSnapshotData('salesOrders', (s) => setSales(s || []));
    const uC = onSnapshotData('customers', (c) => setCustomers(c || []));
    (async () => {
      try {
        const data = await getData('modelCodes');
        const map = (data || []).reduce((acc, obj) => {
          acc[`${obj.strFactory} - ${obj.strModel}`] = obj.strModelName;
          return acc;
        }, {});
        setModelCodes(map);
      } finally { setLoading(false); }
    })();
    return () => { uV && uV(); uS && uS(); uC && uC(); };
  }, [onSnapshotData, getData]);

  // determine role & load allowed customers / upfitter
  useEffect(() => {
    const role = getUserRoleFromCurrentUser(currentUser);
    setUserRole(role || null);

    const fetchMyAssignments = async () => {
      // default "loaded" states if not needed
      if (!currentUser?.uid) {
        setAllowedCustNbrs([]);
        setAllowedUpfitterNbr('');
        return;
      }

      if (role === 'Customer' || role === 'Upfitter') {
        const allUsers = await getData('users');
        const me = (allUsers || []).find(u => u.id === currentUser.uid);

        if (role === 'Customer') {
          setAllowedCustNbrs(Array.isArray(me?.customers) ? me.customers : []);
          setAllowedUpfitterNbr(''); // not used
        } else if (role === 'Upfitter') {
          // user.upfitter is a single strUpfitterNbr
          setAllowedUpfitterNbr(me?.upfitter ?? ''); // '' or 'None' means none assigned
          setAllowedCustNbrs([]); // not used
        }
      } else {
        // other roles: gates not used but mark as loaded
        setAllowedCustNbrs([]);
        setAllowedUpfitterNbr('');
      }
    };
    fetchMyAssignments();
  }, [currentUser, getData]);

  // lookups
  const soByNumber = useMemo(() => {
    const m = new Map();
    (salesOrders || []).forEach(so => m.set(so.strSalesOrdNbr, so));
    return m;
  }, [salesOrders]);

  const custByNbr = useMemo(() => {
    const m = new Map();
    (customers || []).forEach(c => m.set(c.strCustNbr, c));
    return m;
  }, [customers]);

  // rows (with Customer/Upfitter role filtering)
  const rows = useMemo(() => {
    const beg = startOfDay(beginDate);
    const end = endOfDay(endDate);

    const base = [];

    (vehicles || []).forEach(v => {
      const eta = toDate(v.dtManETA);
      if (!eta) return;
      if (eta < beg || eta > end) return;

      // Upfitter role filter: must be one of v.arrUpfitters
      if (userRole === 'Upfitter') {
        if (allowedUpfitterNbr === null) return; // not loaded yet
        const upfits = Array.isArray(v.arrUpfitters) ? v.arrUpfitters : [];
        const isMine = upfits.some(u =>
          String(u?.upfitterNbr || '').trim() === String(allowedUpfitterNbr || '').trim()
        );
        //Switch this if the user can be assigned to multiple upfitters
        //const isMine = upfits.some(u => allowedUpfittersArray.includes(u.upfitterNbr));

        if (!isMine) return;
      }

      const so = soByNumber.get(v.strSalesOrdNbr);
      if (!so) return;
      if (onlyActiveOrders && so.strSalesOrdStatus !== 'Active') return;

      // Customer role filter: must match allowedCustNbrs
      if (userRole === 'Customer') {
        if (!Array.isArray(allowedCustNbrs)) return; // not loaded yet
        if (!allowedCustNbrs.includes(so.strCustNbr)) return;
      }

      const customer = custByNbr.get(so.strCustNbr);
      base.push({
        id: v.id,
        soNum: v.strSalesOrdNbr || '',
        stock: stockDisplay(v),
        orderNum: v.strOrdNbr || '',
        customer: customer?.strCustName || '',
        yr: v.strYR || '',
        make: v.strMake || '',
        model: resolveModelName(v, modelCodes),
        eta,
      });
    });

    base.sort((a, b) => a.eta - b.eta || a.soNum.localeCompare(b.soNum) || a.stock.localeCompare(b.stock));
    return base;
  }, [vehicles, beginDate, endDate, soByNumber, custByNbr, modelCodes, onlyActiveOrders, userRole, allowedCustNbrs, allowedUpfitterNbr]);

  // grouping
  const grouped = useMemo(() => {
    if (groupBy === 'date') return { '__ALL__': rows };

    const map = new Map();
    const keyFn =
      groupBy === 'customer'
        ? (r) => r.customer || '(Unknown Customer)'
        : groupBy === 'model'
          ? (r) => r.model || '(Unknown Model)'
          : (r) => r.soNum || '(Unknown SO #)'; // salesOrder

    rows.forEach(r => {
      const k = keyFn(r);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    });

    const sortFn = groupBy === 'salesOrder'
      ? ([a], [b]) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      : ([a], [b]) => a.localeCompare(b);

    return Object.fromEntries([...map.entries()].sort(sortFn));
  }, [rows, groupBy]);

  const headerTitle =
    groupBy === 'date' ? 'ETA Report By Date' :
    groupBy === 'customer' ? 'ETA Report By Customer' :
    groupBy === 'model' ? 'ETA Report By Model' :
    'ETA Report By Sales Order';

  // wait for gates to load
  const ready = !loading &&
    (userRole !== 'Customer' || allowedCustNbrs !== null) &&
    (userRole !== 'Upfitter' || allowedUpfitterNbr !== null);

  if (!ready) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const TableHeader = () => (
    <thead>
      <tr>
        <th>SO #</th>
        <th>Stock #</th>
        <th>Order #</th>
        <th>Customer</th>
        <th>Yr</th>
        <th>Make</th>
        <th>Model</th>
        <th>ETA</th>
      </tr>
    </thead>
  );

  const noUpfitterAssigned =
    userRole === 'Upfitter' &&
    (allowedUpfitterNbr === '' || String(allowedUpfitterNbr).toLowerCase() === 'none');

  return (
    <Card>
      <Card.Body>
        <div className="text-center mb-2">
          <div className="fw-bold">Hardy Automotive Fleet Group</div>
          <div>{headerTitle}</div>
          <div className="fw-bold">{fmtRange(beginDate, endDate)}</div>
        </div>

        <Row className="g-3 align-items-end mb-3">
          <Col xs={12} md={3}>
            <Form.Label>Begin Date</Form.Label>
            <Form.Control
              type="date"
              value={formatYYYYMMDD(beginDate)}
              onChange={(e) => {
                const d = startOfDay(parseDateInput(e.target.value));
                setBeginDate(d > endDate ? d : d);
              }}
            />
          </Col>
          <Col xs={12} md={3}>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={formatYYYYMMDD(endDate)}
              onChange={(e) => {
                const d = endOfDay(parseDateInput(e.target.value));
                setEndDate(d < beginDate ? d : d);
              }}
            />
          </Col>
          <Col xs={12} md={3}>
            <Form.Label>Grouping</Form.Label>
            <Form.Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              {GROUPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
          </Col>
        </Row>

        <Table bordered responsive>
          <TableHeader />
          <tbody>
            {groupBy === 'date' && rows.map(r => (
              <tr key={`${r.id}-${r.soNum}-${r.stock}`}>
                <td>{r.soNum}</td>
                <td>{r.stock}</td>
                <td>{r.orderNum}</td>
                <td>{r.customer}</td>
                <td>{r.yr}</td>
                <td>{r.make}</td>
                <td>{r.model}</td>
                <td>{fmtMD(r.eta)}</td>
              </tr>
            ))}

            {groupBy !== 'date' && Object.entries(grouped).map(([groupName, items]) => (
              <React.Fragment key={groupName}>
                <tr>
                  <td colSpan={8} className="fw-bold pt-3">
                    {groupBy === 'salesOrder' ? `SO # ${groupName}` : groupName}
                  </td>
                </tr>
                {items.map(r => (
                  <tr key={`${r.id}-${r.soNum}-${r.stock}`}>
                    <td>{r.soNum}</td>
                    <td>{r.stock}</td>
                    <td>{r.orderNum}</td>
                    <td>{r.customer}</td>
                    <td>{r.yr}</td>
                    <td>{r.make}</td>
                    <td>{r.model}</td>
                    <td>{fmtMD(r.eta)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}

            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-muted text-center">
                  {userRole === 'Customer' && Array.isArray(allowedCustNbrs) && allowedCustNbrs.length === 0
                    ? 'No customers are assigned to your account.'
                    : userRole === 'Upfitter' && noUpfitterAssigned
                      ? 'No upfitter is assigned to your account.'
                      : 'No vehicles in this date range.'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}
